import { Switch as HeadlessSwitch } from '@headlessui/react';
import { useField } from 'formik';
import { clsx } from 'clsx';
import React from 'react';

export interface SwitchProps {
  name: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
}

export function Switch({
  name,
  label,
  description,
  disabled = false,
  className,
  checked: controlledChecked,
  onChange: controlledOnChange,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const [field, , helpers] = useField({ name, type: 'checkbox' });

  const isChecked = controlledChecked !== undefined ? controlledChecked : field.value || false;

  const handleChange = (checked: boolean) => {
    helpers.setValue(checked);
    if (controlledOnChange) {
      const syntheticEvent = {
        target: { checked, name },
        currentTarget: { checked, name },
      } as React.ChangeEvent<HTMLInputElement>;
      controlledOnChange(syntheticEvent);
    }
  };

  // If no label, render compact version (for use in tables, etc.)
  if (!label) {
    return (
      <HeadlessSwitch
        checked={isChecked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel || name}
        className={clsx(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          isChecked ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-dark-700',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <span
          className={clsx(
            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
            isChecked ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </HeadlessSwitch>
    );
  }

  return (
    <HeadlessSwitch.Group>
      <div className={clsx('flex items-center justify-between', className)}>
        <div className="flex-1">
          <HeadlessSwitch.Label
            className={clsx(
              'cursor-pointer text-sm font-medium',
              disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'
            )}
          >
            {label}
          </HeadlessSwitch.Label>
          {description && (
            <HeadlessSwitch.Description className="text-sm text-gray-500 dark:text-gray-400">
              {description}
            </HeadlessSwitch.Description>
          )}
        </div>
        <HeadlessSwitch
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          className={clsx(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            isChecked ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-dark-700',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <span
            className={clsx(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
              isChecked ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </HeadlessSwitch>
      </div>
    </HeadlessSwitch.Group>
  );
}
